import React, {useRef, useState} from 'react';
import App from "./App";
import Register from "./components/Account/Register";
import Login from "./components/Account/Login";
import Sso from "./components/Account/Sso";
import Recovery from "./components/Account/Recovery";

import {Route, Routes, useLocation, useNavigate, Navigate} from 'react-router-dom';
import {useTranslation} from "react-i18next";
import {Toast} from "primereact/toast";
import ErrorHandler from "./utils/ErrorHandler";
import {AuthRequiredRoute} from "./AuthRequiredRoute";
import {AdminRequiredRoute} from "./AdminRequiredRoute";
import Dashboard from "./components/Dashboard/Dashboard";
import Account from "./components/Account/Account";
import ChangePassword from "./components/Account/ChangePassword";
import Campaign from "./components/Campaign/Campaign";
import Product from "./components/Product/Product";
import SellerWorkspace from "./components/Sellers/SellerWorkspace";
import PriceComparison from "./components/Dashboard/PriceComparison";
import PriceComparisonSummary from "./components/Dashboard/PriceComparisonSummary";
import CategoryPriceComparison from "./components/Dashboard/CategoryPriceComparison";
import WeeklyPriceComparison from "./components/Dashboard/WeeklyPriceComparison";
import NCheapestSellerPriceComparison from "./components/Dashboard/NCheapestSellerPriceComparison";
import FilteredSellersPriceComparison from "./components/Dashboard/FilteredSellersPriceComparison";
import PriceStockChanges from "./components/Dashboard/PriceStockChanges";
import SalesPerformance from "./components/Dashboard/SalesPerformance";
import PriceViolations from "./components/Dashboard/PriceViolations";
import SellersSummary from "./components/Dashboard/SellersSummary";
import BrandComparison from "./components/Dashboard/BrandComparison";
import UnauthorizedSellers from "./components/Dashboard/UnauthorizedSellers";
import DropShippingLoss from "./components/Dashboard/DropShippingLoss";
import DynamicPricing from "./components/Dashboard/DynamicPricing";
import ExcelReports from "./components/Dashboard/ExcelReports";
import ProductDetail from "./components/Product/ProductDetail";
import SiteDefinitions from "./components/Admin/SiteDefinitions";
import Cache from "./components/Admin/Cache";
import HeadlessBrowser from "./components/Admin/HeadlessBrowser";
import ScrapingJobs from "./components/Admin/ScrapingJobs";
import BotGroups from "./components/Admin/BotGroups";
import PricingPlans from "./components/Admin/PricingPlans";
import JobScheduling from "./components/Admin/JobScheduling";
import Impersonation from "./components/Admin/Impersonation";
import OverviewRequest from "./components/Admin/OverviewRequest";
import AiJobsMonitor from "./components/Admin/AiJobsMonitor";
import OverviewKPI from "./components/Admin/OverviewKPI";
import URLStatistics from "./components/Admin/URLStatistics";
import NewWebsites from "./components/Admin/NewWebsites";
import Income from "./components/Admin/Income";
import Expense from "./components/Admin/Expense";
import PaymentLog from "./components/Admin/PaymentLog";
import RequestProbes from "./components/Admin/RequestProbes";
import User from "./components/Admin/User";
import PriceRestMessage from "./components/Admin/PriceRestMessage";
import AnnouncementManagement from "./components/Admin/AnnouncementManagement";
import Error from "./components/Common/Error";
import Page404 from "./Page404";
import SpecialFields from "./components/Admin/SpecialFields";
import Canary from "./components/Admin/Canary";
import ProxyMonitor from "./components/Admin/ProxyMonitor";
import ProxyEfficiency from "./components/Admin/ProxyEfficiency";
import SchedulerMonitor from "./components/Admin/SchedulerMonitor";
import SystemHealth from "./components/Admin/SystemHealth";
import DailyPriceComparison from "./components/Dashboard/DailyPriceComparison";
import PriceIndex from "./components/Dashboard/PriceIndex";
import ReportsHub from "./components/Dashboard/ReportsHub";
import ManagementHub from "./components/Admin/ManagementHub";
import Queue from "./components/Admin/Queue";
import OnboardingPage from "./components/Onboarding/OnboardingPage";
import AdminPageHeader from "./components/Admin/AdminPageHeader";
import RepricingRulesPage from "./components/Pricing/RepricingRulesPage";
import CampaignQuality from "./components/Admin/CampaignQuality";
import EmailCampaign from "./components/Admin/EmailCampaign/EmailCampaign";
import Announcements from "./components/Announcements";

export const AppContext = React.createContext();

const HomePage = (props) => {

    const {t} = useTranslation();
    const navigate  = useNavigate();
    const location = useLocation();
    const [error, setError] = useState(null);

    let toast = useRef(null);

    const url = window.location.hostname;

    let host = "pricerest";
    let appName = "PriceRest";
    let companyName = t('company_name');
    let supportEmail = "info@pricerest.com";

    if (url.toLowerCase().indexOf('ecomscanner') !== -1) {
        host = "ecomscanner";
        appName = "eCOMSCANNER";
        companyName = "";
        const favicon = document.getElementById("favicon"); // Accessing favicon element
        favicon.href = "https://intpact.com/uploads/lcwebsite/1/favicon_image/favicon.ico";
        document.title = t('login.auth_info_msg_1', {appName: appName});
        supportEmail = "support@intpact.com";
    }
    else
    {
        document.title = t('login.auth_info_msg_1', {appName: appName});
    }

    return (
        <AppContext.Provider value={{host, appName, supportEmail, companyName,
            setError,
            t, toast, navigate, location}}>
            <div>
                <Toast ref={toast} baseZIndex={100000} />
                <ErrorHandler error={error} />

                <Routes>
                    <Route path="/login" element={<Login/>}/>
                    <Route path="/sso" element={<Sso/>}/>
                    <Route path="/register" element={<Register/>}/>
                    <Route path="/recovery" element={<Recovery/>}/>
                    <Route path="*" element={<App/>} >
                        <Route path="" element={<AuthRequiredRoute><Dashboard /></AuthRequiredRoute>} />
                        <Route path="account" element={<AuthRequiredRoute><Account /></AuthRequiredRoute>}/>
                        <Route path="changepassword" element={<AuthRequiredRoute><ChangePassword /></AuthRequiredRoute>}/>
                        <Route path="campaign" element={<AuthRequiredRoute><Campaign /></AuthRequiredRoute>} />
                        {/* Route is open to any logged-in user; intentionally NOT in the menu
                            yet (reached via direct #/onboarding link during rollout). */}
                        <Route path="onboarding" element={<AuthRequiredRoute><OnboardingPage /></AuthRequiredRoute>} />
                        <Route path="product" element={<AuthRequiredRoute><Product /></AuthRequiredRoute>} />
                        <Route path="sellers" element={<AuthRequiredRoute><SellerWorkspace /></AuthRequiredRoute>} />
                        <Route path="repricing_rules" element={<AuthRequiredRoute><RepricingRulesPage /></AuthRequiredRoute>} />
                        <Route path="report" element={<AuthRequiredRoute><ReportsHub /></AuthRequiredRoute>} />
                        <Route path="report/price_comparison" element={<AuthRequiredRoute><PriceComparison /></AuthRequiredRoute>} />
                        <Route path="report/price_comparison_summary" element={<AuthRequiredRoute><PriceComparisonSummary /></AuthRequiredRoute>} />
                        <Route path="report/category_price_comparison" element={<AuthRequiredRoute><CategoryPriceComparison /></AuthRequiredRoute>} />
                        <Route path="report/daily_price_comparison" element={<AuthRequiredRoute><DailyPriceComparison /></AuthRequiredRoute>} />
                        <Route path="report/weekly_price_comparison" element={<AuthRequiredRoute><WeeklyPriceComparison /></AuthRequiredRoute>} />
                        <Route path="report/n_cheapest_seller_price_comparison" element={<AuthRequiredRoute><NCheapestSellerPriceComparison /></AuthRequiredRoute>} />
                        <Route path="report/filtered_seller_price_comparison" element={<AuthRequiredRoute><FilteredSellersPriceComparison /></AuthRequiredRoute>} />
                        <Route path="report/price_changes" element={<AuthRequiredRoute><PriceStockChanges /></AuthRequiredRoute>} />
                        <Route path="report/sales" element={<AuthRequiredRoute><SalesPerformance /></AuthRequiredRoute>} />
                        <Route path="report/violations" element={<AuthRequiredRoute><PriceViolations /></AuthRequiredRoute>} />
                        <Route path="report/sellers_summary" element={<AuthRequiredRoute><SellersSummary /></AuthRequiredRoute>} />
                        <Route path="report/brand_comparison" element={<AuthRequiredRoute><BrandComparison /></AuthRequiredRoute>} />
                        <Route path="report/price_index" element={<AuthRequiredRoute><PriceIndex /></AuthRequiredRoute>} />
                        <Route path="report/unauthorized_sellers" element={<AuthRequiredRoute><UnauthorizedSellers /></AuthRequiredRoute>} />
                        <Route path="report/drop_shipping_loss" element={<AuthRequiredRoute><DropShippingLoss /></AuthRequiredRoute>} />
                        <Route path="report/dynamic_pricing" element={<AuthRequiredRoute><DynamicPricing /></AuthRequiredRoute>} />
                        <Route path="report/excel_reports" element={<AuthRequiredRoute><ExcelReports /></AuthRequiredRoute>} />
                        <Route path="announcements" element={<AuthRequiredRoute><Announcements /></AuthRequiredRoute>}/>
                        <Route path="productdetail" element={<AuthRequiredRoute><ProductDetail /></AuthRequiredRoute>} />
                        <Route path="management" element={<AdminRequiredRoute><ManagementHub /></AdminRequiredRoute>}/>
                        <Route path="websites" element={<AdminRequiredRoute><SiteDefinitions /></AdminRequiredRoute>}/>
                        <Route path="cache" element={<AdminRequiredRoute><Cache /></AdminRequiredRoute>}/>
                        <Route path="headless_browsers" element={<AdminRequiredRoute><HeadlessBrowser /></AdminRequiredRoute>}/>
                        <Route path="special_fields" element={<AdminRequiredRoute><SpecialFields /></AdminRequiredRoute>}/>
                        <Route path="canary" element={<AdminRequiredRoute><Canary /></AdminRequiredRoute>}/>
                        <Route path="proxy_monitor" element={<AdminRequiredRoute><ProxyMonitor /></AdminRequiredRoute>}/>
                        <Route path="proxy_efficiency" element={<AdminRequiredRoute><ProxyEfficiency /></AdminRequiredRoute>}/>
                        <Route path="scheduler_monitor" element={<AdminRequiredRoute><SchedulerMonitor /></AdminRequiredRoute>}/>
                        <Route path="system_health" element={<AdminRequiredRoute><SystemHealth /></AdminRequiredRoute>}/>
                        <Route path="request_analytics" element={<Navigate to="/canary" replace/>}/>
                        <Route path="scraping_job" element={<AdminRequiredRoute><ScrapingJobs /></AdminRequiredRoute>}/>
                        <Route path="bot_groups" element={<AdminRequiredRoute><BotGroups /></AdminRequiredRoute>}/>
                        <Route path="pricing_plans" element={<AdminRequiredRoute><PricingPlans /></AdminRequiredRoute>}/>
                        <Route path="campaign_quality" element={<AdminRequiredRoute><CampaignQuality /></AdminRequiredRoute>}/>
                        <Route path="job_scheduling" element={<AdminRequiredRoute><JobScheduling /></AdminRequiredRoute>}/>
                        <Route path="impersonation" element={<AuthRequiredRoute><Impersonation /></AuthRequiredRoute>}/>
                        <Route path="overview_request" element={<AdminRequiredRoute><div className="card management-page"><AdminPageHeader title={t('dashboard.overview_request')} subtitle={t('menu.management_hub.page.overview_request')} /><OverviewRequest /></div></AdminRequiredRoute>}/>
                        <Route path="ai_jobs" element={<AdminRequiredRoute><div className="card management-page"><AdminPageHeader title={t('dashboard.ai_jobs')} subtitle={t('menu.management_hub.page.ai_jobs')} /><AiJobsMonitor /></div></AdminRequiredRoute>}/>
                        <Route path="queue" element={<AdminRequiredRoute><div className="card management-page"><AdminPageHeader title={t('menu_item.queue')} subtitle={t('menu.management_hub.page.queue')} /><Queue /></div></AdminRequiredRoute>}/>
                        <Route path="overview_kpi" element={<AdminRequiredRoute><div className="card management-page"><AdminPageHeader title={t('dashboard.overview_kpi')} subtitle={t('menu.management_hub.page.overview_kpi')} /><OverviewKPI /></div></AdminRequiredRoute>}/>
                        <Route path="url_statistics" element={<AdminRequiredRoute><div className="card management-page"><AdminPageHeader title={t('dashboard.url_statistics')} subtitle={t('menu.management_hub.page.url_statistics')} /><URLStatistics /></div></AdminRequiredRoute>}/>
                        <Route path="new_websites" element={<AdminRequiredRoute><div className="card management-page"><AdminPageHeader title={t('dashboard.new_websites')} subtitle={t('menu.management_hub.page.new_websites')} /><NewWebsites /></div></AdminRequiredRoute>}/>
                        <Route path="income" element={<AdminRequiredRoute><div className="card management-page"><AdminPageHeader title={t('menu_item.income')} subtitle={t('menu.management_hub.page.income')} /><Income /></div></AdminRequiredRoute>}/>
                        <Route path="expense" element={<AdminRequiredRoute><div className="card management-page"><AdminPageHeader title={t('menu_item.expense')} subtitle={t('menu.management_hub.page.expense')} /><Expense /></div></AdminRequiredRoute>}/>
                        <Route path="payment_log" element={<AdminRequiredRoute><div className="card management-page"><AdminPageHeader title={t('menu_item.payment_log')} subtitle={t('menu.management_hub.page.payment_log')} /><PaymentLog /></div></AdminRequiredRoute>}/>
                        <Route path="request_probes" element={<AdminRequiredRoute><div className="card management-page"><AdminPageHeader title={t('menu_item.request_probes')} subtitle={t('menu.management_hub.page.request_probes')} /><RequestProbes /></div></AdminRequiredRoute>}/>
                        <Route path="user" element={<AdminRequiredRoute><User /></AdminRequiredRoute>}/>
                        <Route path="email_campaign" element={<AdminRequiredRoute><EmailCampaign /></AdminRequiredRoute>}/>
                        <Route path="message" element={<AdminRequiredRoute><PriceRestMessage /></AdminRequiredRoute>}/>
                        <Route path="announcement_management" element={<AdminRequiredRoute><AnnouncementManagement /></AdminRequiredRoute>}/>
                        <Route path="error" element={<AuthRequiredRoute><Error /></AuthRequiredRoute>}/>

                        <Route path="*" element={<Page404 />}/>
                    </Route>
                </Routes>
            </div>
        </AppContext.Provider>

    );

};
export default HomePage;
